import { useMemo } from 'react';
import { resolveCampaignView, type CampaignView } from '../selectors/campaignView';
import { combine, useLogFailure } from './combine';
import {
  useBlastRadiusScores,
  useBusinessElements,
  useInfrastructureElements,
  useRemediations,
  useSecurityEvents,
  useThreatCandidates,
  useThreatElements,
  useThreatEvents,
} from './hooks';

export interface CampaignData {
  /** The campaign the id opens: null while reading, on a failed read, and when nothing has that id. */
  view: CampaignView | null;
  /** The first read is under way. */
  loading: boolean;
  /** A read failed. */
  error: boolean;
}

/**
 * One campaign for the campaign screen, from the shared cache and the one refresh clock. `id` is a campaign's id or
 * an incident's: an incident inside a campaign opens that campaign (the view's `id` says which).
 */
export function useCampaignData(id: string): CampaignData {
  const elements = useThreatElements();
  const threatEvents = useThreatEvents();
  const candidates = useThreatCandidates();
  const remediations = useRemediations();
  const infrastructure = useInfrastructureElements();
  const business = useBusinessElements();
  const blastScores = useBlastRadiusScores();
  const securityEvents = useSecurityEvents();
  const all = [
    elements,
    threatEvents,
    candidates,
    remediations,
    infrastructure,
    business,
    blastScores,
    securityEvents,
  ];
  const state = combine(all);

  const view = useMemo(() => {
    if (state.failed || !state.ready) return null;
    return resolveCampaignView(id, {
      threatEvents: threatEvents.data ?? [],
      candidates: candidates.data ?? [],
      elements: elements.data ?? [],
      remediations: remediations.data ?? [],
      infrastructure: infrastructure.data ?? [],
      business: business.data ?? [],
      blastScores: blastScores.data ?? [],
      securityEvents: securityEvents.data ?? [],
    });
    // The records themselves: a read that brings back the same ones gives the same references.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    state.failed,
    state.ready,
    threatEvents.data,
    candidates.data,
    elements.data,
    remediations.data,
    infrastructure.data,
    business.data,
    blastScores.data,
    securityEvents.data,
  ]);

  useLogFailure('Campaign could not be loaded', all);

  return { view, loading: state.loading, error: state.error };
}
